import React, { Component } from "reactn";

import {
    DialogTitle,
    DialogActions,
    Button,
    LinearProgress
} from "@material-ui/core";

import { app } from "js/namespaces";
import { _ } from "js/vendor";
import { TaskType, TaskState, AssetType, ImageErrorType } from "common/constants";
import { RowGrid } from "js/react/components/LayoutGrid";
import { BeautifulDialog, DialogContent, ShowUpgradeDialog, ShowOfflineDialog } from "js/react/components/Dialogs/BaseDialog";
import shouldShowProBadge from "js/core/utilities/shouldShowProBadge";
import { FeatureType } from "js/core/models/features";
import { trackActivity } from "js/core/utilities/utilities";
import { ds } from "js/core/models/dataService";
import getLogger from "js/core/logger";
import * as geom from "js/core/utilities/geom";
import Api from "js/core/api";
import { UIController } from "js/editor/dialogs/UIController";
import { Gap10 } from "js/react/components/Gap";
import { UIActionType, isUIActionRestricted } from "js/core/uiActionsRestrictors";
import MediaDialog from "./MediaImporters/MediaDialog";
import { handleFilePaste } from "./importVideoHelpers";
import isConnected from "js/core/utilities/isConnected";
import { getImageType, isValidMediaType } from "js/core/utilities/imageUtilities";
import { UpgradePlanDialogType } from "../MarketingDialogs/UpgradePlanDialog";
import { uploadFileAndCreateTask } from "js/core/services/tasks";

import { AVAILBLE_IMAGE_SOURCES, AVAILBLE_VIDEO_SOURCES, FILE_TYPES, MEDIA_DIVIDER_TYPES, MEDIA_TYPES } from "./MediaImporters/PaneOptions";

const logger = getLogger();

export class ImportMediaDialog extends Component {
    constructor(props) {
        super(props);
        this.getVideoAssetDebounced = _.debounce(this.getVideoAsset, 500);
    }

    state = {
        asset: null,
        uiState: "choose",
        uploadState: TaskState.PREPARING,
        isUploading: false,
        progress: 0,
        label: "",
        error: null,
    };

    componentDidMount() {
        window.addEventListener("paste", evt => handleFilePaste(evt, this.uploadFile));
    }

    componentWillUnmount() {
        window.removeEventListener("paste", evt => handleFilePaste(evt, this.uploadFile));
    }

    uploadStateText = () => {
        const {
            uploadState
        } = this.state;

        switch (uploadState) {
            case TaskState.PREPARING:
                return <DialogTitle>Uploading Media</DialogTitle>;
            case TaskState.PROCESSING:
                return <DialogTitle>Processing Media</DialogTitle>;
            case TaskState.ERROR:
                return <DialogTitle>Error</DialogTitle>;
            case TaskState.FINISHED:
                return <DialogTitle>Your Media Is Ready</DialogTitle>;
            default:
                return null;
        }
    }

    getFileTypes() {
        const {
            videoOnly = false,
            imageOnly = false,
        } = this.props;

        const allowImage = (imageOnly || !videoOnly) && !isUIActionRestricted(UIActionType.ADD_ASSET, { assetType: AssetType.IMAGE });

        let fileTypes = [FILE_TYPES.VIDEO, FILE_TYPES.IMAGE];
        if (videoOnly) {
            fileTypes = [FILE_TYPES.VIDEO];
        }
        if (imageOnly) {
            fileTypes = [FILE_TYPES.IMAGE];
        }
        if (!allowImage) {
            fileTypes = fileTypes.filter(type => type !== FILE_TYPES.IMAGE);
        }

        return fileTypes;
    }

    getImagesResourceTypes({ sources, allowOneDrive, allowHostedVideo, allowBynder, allowBox, allowGoogleDrive, allowDropbox }) {
        let sourceTypes = sources;
        if (!allowOneDrive) sourceTypes = sourceTypes.filter(type => type !== MEDIA_TYPES.ONE_DRIVE);
        if (!allowDropbox) sourceTypes = sourceTypes.filter(type => type !== MEDIA_TYPES.DROPBOX);
        if (!allowBynder) sourceTypes = sourceTypes.filter(type => type !== MEDIA_TYPES.BYNDER);
        if (!allowBox) sourceTypes = sourceTypes.filter(type => type !== MEDIA_TYPES.BOX);
        if (!allowGoogleDrive) sourceTypes = sourceTypes.filter(type => type !== MEDIA_TYPES.GOOGLE_DRIVE);
        if (!allowHostedVideo) sourceTypes = sourceTypes.filter(type => type !== MEDIA_TYPES.VIMEO || type !== MEDIA_TYPES.YOUTUBE || type !== MEDIA_DIVIDER_TYPES.DIVIDER);

        // Incase user is not allowed to use media integration, we need to filter out the media integration option
        if (!allowBynder && !allowBox && !allowGoogleDrive) {
            sourceTypes.push(MEDIA_TYPES.UPGRADE);
        }

        return sourceTypes;
    }

    getSourceTypes() {
        const {
            videoOnly = false,
            imageOnly = false,
        } = this.props;
        const workspaceId = UIController.getWorkspaceId();

        const allowHostedVideo = !isUIActionRestricted(UIActionType.ADD_ASSET, { assetType: AssetType.VIDEO, source: "hosted" });
        const allowOneDrive = app.user.features.isFeatureEnabled(FeatureType.ONE_DRIVE_INTEGRATION, workspaceId);
        const allowDropbox = app.user.features.isFeatureEnabled(FeatureType.DROPBOX_INTEGRATION, workspaceId);
        const allowBynder = app.user.features.isFeatureEnabled(FeatureType.BYNDER_INTEGRATION, workspaceId);
        const allowBox = app.user.features.isFeatureEnabled(FeatureType.BOX_INTEGRATION, workspaceId);
        const allowGoogleDrive = app.user.features.isFeatureEnabled(FeatureType.GOOGLE_DRIVE_INTEGRATION, workspaceId);

        const sources = videoOnly ? AVAILBLE_VIDEO_SOURCES : AVAILBLE_IMAGE_SOURCES;
        return this.getImagesResourceTypes({ sources, allowOneDrive, allowHostedVideo, allowBynder, allowBox, allowGoogleDrive, allowDropbox });
    }

    render() {
        const {
            label,
            progress,
            uiState,
            uploadState,
            asset
        } = this.state;
        const {
            closeDialog,
            videoOnly = false,
            imageOnly = false,
        } = this.props;

        const allowUploadedVideo = (videoOnly || !imageOnly) && !isUIActionRestricted(UIActionType.ADD_ASSET, { assetType: AssetType.VIDEO, source: "upload" });
        const title = videoOnly ? "Import Video" : "Import Media";
        let warningText = "NOTE: Max file size is 500MB";
        let subTitle = "";
        if (!videoOnly) {
            subTitle = allowUploadedVideo ? "Import a video, image, or gif" : "Import an image or GIF";
            warningText = allowUploadedVideo ? "NOTE: Max file size for images is 50MB and for videos is 500MB" : "NOTE: Max file size for images is 50mb";
        }

        const fileTypes = this.getFileTypes();
        const sourceTypes = this.getSourceTypes();

        switch (uiState) {
            case "choose":
                return (<MediaDialog
                    title={title}
                    subTitle={subTitle}
                    uploadFile={this.uploadFile}
                    onConfirm={this.onConfirm}
                    asset={asset}
                    closeDialog={closeDialog}
                    getVideoAssetDebounced={this.getVideoAssetDebounced}
                    allowedFileTypes={fileTypes}
                    allowedSourceTypes={sourceTypes}
                    warningText={warningText}
                />);
            case "media-upload":
                return (
                    <BeautifulDialog maxWidth="md" closeDialog={closeDialog} preventClose>
                        {this.uploadStateText()}
                        <DialogContent className="import-media-dialog">
                            <RowGrid item xs={12}>
                                <div style={{ width: "100%" }}>{label}</div>
                            </RowGrid>
                            <Gap10 />
                            <RowGrid item xs={12}>
                                <LinearProgress
                                    value={progress}
                                    variant="determinate"
                                />
                            </RowGrid>
                            {uploadState === TaskState.PREPARING &&
                                <RowGrid item xs={12}>
                                    <div style={{ fontStyle: "italic", fontWeight: 500, fontSize: 16, color: "#666", paddingTop: 20 }}>Hang tight. This might take a minute or so. Please do not close or refresh the browser while your media is uploading.</div>
                                </RowGrid>}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeDialog}>Cancel</Button>
                            <Button color="primary" disabled={!asset} onClick={this.onConfirm}>Add Media</Button>
                        </DialogActions>
                    </BeautifulDialog>
                );
        }
    }

    onConfirm = async () => {
        const { asset } = this.state;

        this.props.onAccept(asset);
        this.props.closeDialog();
    }

    getVideoAsset = async (hostedVideoUrl, videoAssetId) => {
        let error;

        const isUpload = !!videoAssetId;
        const isYoutube = !!hostedVideoUrl && (hostedVideoUrl.includes("youtube") || hostedVideoUrl.includes("youtu.be"));
        const isVimeo = !!hostedVideoUrl && hostedVideoUrl.includes("vimeo");
        const videoType = isUpload ? "upload" : (isVimeo ? "vimeo" : (isYoutube ? "youtube" : hostedVideoUrl));

        const {
            backgroundVideoOnly = false,
        } = this.props;

        // Default the video size
        let size = new geom.Size(800, 450);
        let duration = 100;

        const assetAttributes = {};

        if (isUpload) {
            try {
                const assetModel = await ds.assets.getAssetById(videoAssetId, "video");
                await assetModel.load();
                Object.assign(assetAttributes, assetModel.attributes);
                size = new geom.Size(assetAttributes.w, assetAttributes.h);
                duration = assetAttributes.duration;
            } catch (err) {
                logger.error(err, "[ImportMediaDialog] getVideoAsset() failed to load video asset", { videoAssetId });
                error = err.message;
            }
        } else {
            try {
                const oembedUrl = isVimeo
                    ? `https://www.vimeo.com/api/oembed.json?url=${encodeURIComponent(hostedVideoUrl)}&byline=false&portrait=false&title=false`
                    : `https://www.youtube.com/oembed?url=${encodeURIComponent(hostedVideoUrl)}&format=json`;
                const response = await Api.oembed.post({ url: oembedUrl });
                size = new geom.Size(response.width, response.height);
                duration = response.duration || duration;
                assetAttributes.url = hostedVideoUrl;
            } catch (err) {
                // Since we are processing a user supplied url, we're going to display errors as warnings
                logger.warn("[ImportMediaDialog] Api.oembed.post() failed", { errorMessage: err.message });
                if (err.message === "Bad oembed request: Bad Request") {
                    error = "Invalid video url";
                } else {
                    error = err.message;
                }
            }
        }

        if (error) {
            this.setState({
                asset: null,
                error
            });
            return;
        }

        trackActivity("Video", "AddedToSlide", null, null, {
            "slide_id": ds.selection.slide?.id,
            "video_type": videoType,
            "url": hostedVideoUrl,
            "id": videoAssetId,
        }, { audit: true });

        const controls = !backgroundVideoOnly && duration > 30;

        this.setState({
            asset: {
                ...assetAttributes,
                hostedVideoUrl,
                id: videoAssetId,
                type: AssetType.VIDEO,
                size,
                assetProps: {
                    muted: true,
                    duration,
                    startTime: 0,
                    endTime: duration,
                    autoPlay: !controls,
                    loop: !controls,
                    speed: 1.0,
                    controls,
                },
            },
            error: null,
        });
    }

    uploadFile = file => {
        if (!isConnected.connected) {
            ShowOfflineDialog();
            return null;
        }

        let mediaValidation = isValidMediaType(file);
        if (mediaValidation.isValidImage) {
            const { imageAsLogo } = this.props;

            const fileName = file.name;

            const props = {
                "slide_id": ds.selection.slide?.id,
                "uploaded_from": file.source ? file.source : "local",
            };
            if (imageAsLogo) {
                trackActivity("Logo", "Upload", fileName, null, props, { audit: true });
            } else {
                trackActivity("Image", "Upload", fileName, null, props, { audit: true });
            }

            let fileType = getImageType(file);
            const imageProperties = {};
            if (imageAsLogo) {
                // force all logos have solid white backgrounds
                imageProperties.hasSolidBackground = true;
                imageProperties.imageBackgroundColor = "#FFFFFF";
            }

            this.setState({
                label: file.name,
                uiState: "media-upload"
            });

            ds.assets
                .getOrCreateImage({
                    file,
                    url: file.url || null,
                    name: fileName,
                    fileType,
                    assetType: imageAsLogo ? AssetType.LOGO : AssetType.IMAGE,
                    metadata: {
                        source: "upload"
                    },
                    ...[{ ...imageProperties }]
                })
                .then(asset => {
                    this.setState({
                        progress: 100,
                        uploadState: TaskState.FINISHED,
                        asset: {
                            url: asset.getUrl && asset.getUrl(),
                            ...asset,
                            ...asset.attributes,
                            type: asset.type,
                            id: asset.id,
                            size: new geom.Size(asset.get("w"), asset.get("h")),
                        },
                    });
                    this.onConfirm();
                })
                .catch(err => {
                    let label;
                    if (err.type === ImageErrorType.SIZE) {
                        label = err.message;
                    } else if (err.type === ImageErrorType.BAD_DATA) {
                        label = "We were unable to upload this image. Please try again.";
                    } else {
                        label = "We are unable to process and read this image.";
                    }
                    this.setState({
                        progress: 0,
                        uploadState: TaskState.ERROR,
                        label,
                    });
                    logger.error(err, "[ImportMediaDialog] uploadFile() failed");
                });
        } else if (mediaValidation.isValidVideo) {
            const uploadDisabled = shouldShowProBadge(FeatureType.VIDEO_UPLOAD, UIController.getWorkspaceId());
            if (uploadDisabled) {
                ShowUpgradeDialog({
                    type: UpgradePlanDialogType.UPGRADE_PLAN,
                    analytics: { cta: "MediaUpload", ...(ds.selection.presentation?.getAnalytics() ?? {}) },
                    workspaceId: UIController.getWorkspaceId()
                });
            } else {
                this.setState({
                    label: file.name,
                    uiState: "media-upload"
                });

                uploadFileAndCreateTask(file, TaskType.VIDEO_UPLOAD, async task => {
                    if (task.state === TaskState.ERROR) {
                        this.setState({
                            progress: 0,
                            uploadState: TaskState.ERROR,
                            label: task.errorMessage
                        });
                        return;
                    }

                    if (task.state === TaskState.PREPARING) {
                        this.setState({
                            progress: task.stateProgressPercents / 2,
                            uploadState: TaskState.PREPARING
                        });
                        return;
                    }

                    if (task.state === TaskState.PROCESSING) {
                        this.setState({
                            progress: task.stateProgressPercents / 2 + 50,
                            uploadState: TaskState.PROCESSING
                        });
                        return;
                    }

                    if (task.state === TaskState.FINISHED) {
                        this.setState({
                            progress: 100,
                            uploadState: TaskState.FINISHED
                        }, () => this.getVideoAsset(null, task.videoAssetId));
                    }
                });
            }
        }
    }
}
